import React from 'react';
import Icon from '../../../components/AppIcon';

const ReactivityMatrix = ({ chemicals }) => {
  const getCompatibilityColor = (compatibility) => {
    const colors = {
      compatible: 'bg-success/20 border-success/40 hover:bg-success/30',
      caution: 'bg-warning/20 border-warning/40 hover:bg-warning/30',
      incompatible: 'bg-destructive/20 border-destructive/40 hover:bg-destructive/30'
    };
    return colors?.[compatibility] || 'bg-muted border-border';
  };

  const getCompatibilityIcon = (compatibility) => {
    const icons = {
      compatible: 'CheckCircle',
      caution: 'AlertTriangle',
      incompatible: 'XCircle'
    };
    return icons?.[compatibility] || 'HelpCircle';
  };

  const compatibilityData = [
    { chem1: 'Acetone', chem2: 'Benzene', compatibility: 'caution', note: 'Store separately' },
    { chem1: 'Acetone', chem2: 'Methanol', compatibility: 'compatible', note: 'Safe to mix' },
    { chem1: 'Benzene', chem2: 'Methanol', compatibility: 'incompatible', note: 'Keep separate' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Reactivity Compatibility Matrix</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Chemical interaction and storage compatibility assessment
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
          <Icon name="Download" size={16} />
          Export Matrix
        </button>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-success/40 border border-success/60"></div>
          <span className="text-sm text-foreground">Compatible</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-warning/40 border border-warning/60"></div>
          <span className="text-sm text-foreground">Caution Required</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-destructive/40 border border-destructive/60"></div>
          <span className="text-sm text-foreground">Incompatible</span>
        </div>
      </div>
      {/* Matrix Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Chemical 1</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Chemical 2</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Compatibility</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Storage Requirements</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {compatibilityData?.map((row, idx) => (
              <tr key={idx} className="hover:bg-muted/20 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-foreground">{row?.chem1}</td>
                <td className="px-4 py-3 text-sm font-medium text-foreground">{row?.chem2}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center">
                    <div className={`p-2 rounded border ${getCompatibilityColor(row?.compatibility)}`}>
                      <Icon
                        name={getCompatibilityIcon(row?.compatibility)}
                        size={20}
                        className={
                          row?.compatibility === 'compatible' ?'text-success'
                            : row?.compatibility === 'caution' ?'text-warning' :'text-destructive'
                        }
                      />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{row?.note}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button className="p-1 hover:bg-muted rounded transition-colors" title="View Details">
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-1 hover:bg-muted rounded transition-colors" title="Generate Report">
                      <Icon name="FileText" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Info Box */}
      <div className="mt-6 bg-accent/5 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-1">Storage Guidelines</h4>
            <p className="text-sm text-muted-foreground">
              Follow the reactivity matrix for safe chemical storage. Incompatible chemicals must be stored in separate locations
              with appropriate containment measures. Regular compatibility assessments are required for regulatory compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactivityMatrix;